import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Heading, Input, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import DatePicker from "react-datepicker";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST, COLORS } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectMarketType from "@/components/Selects/SelectMarketType";
import SelectInspector from "@/components/Selects/SelectInspector";
import SelectShip from "@/components/Selects/SelectShip";
import SelectCompanyFullType from "@/components/Selects/SelectCompanyFullType";
import SelectAgent from "@/components/Selects/SelectAgent";
import SelectNominationApprovalStatus from "@/components/Selects/SelectNominationApprovalStatus";
import Loader from "@/components/Layouts/Loader";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { inspectionNominationApprovalSchema } from "@/schemas/inspectionNominationApprovalSchema";
import { getDataInspectorByType } from "@/services/inspectorService";
import { getDataVessel } from "@/services/vesselService";
import { getDataRef } from "@/services/refDataService";
import { getDataAgent } from "@/services/agentService";
import { getInspectorPersonData } from "@/services/inspectorPersonService";
import { getNominationById, putStatusNominationInspectingCompany } from "@/services/nominationService";
import SelectOperationType from "@/components/Selects/SelectOperationType";
import SelectVesselType from "@/components/Selects/SelectVesselType";

const InspectingNominationReview = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [listInspectorCompany, setListInspectorCompany] = useState([]) as any;
    const [listInspectorPerson, setListInspectorPerson] = useState([]) as any;
    const [listRawInspector, setListRawInspector] = useState([]) as any;
    const [listRawShipName, setListRawShipName] = useState([]) as any;
    const [listShipName, setListShipName] = useState([]) as any;
    const [listRawAgent, setListRawAgent] = useState([]) as any;
    const [listOperations, setListOperations] = useState([]) as any;
    const [listTypeOfVessels, setListTypeOfVessels] = useState([]) as any;
    const [listMarket, setListMarket] = useState([]) as any;
    const [listSireType, setListSireType] = useState([]) as any;
    const [listCargo, setListCargo] = useState([]) as any;
    const [listMeansOfAccess, setListMeansOfAccess] = useState([]) as any;
    const [listAgent, setListAgent] = useState([]) as any;
    //const [isAgentDisabled, setIsAgentDisabled] = useState(false);
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [defaultType, setDefaultType] = useState<string>('');
    const [defaultCompanyInspectionAddressDetail, setDefaultCompanyInspectionAddressDetail] = useState('');
    const [defaultCompanyInspectionPhoneDetail, setDefaultCompanyInspectionPhoneDetail] = useState('');
    const [defaultRawAgent, setDefaultRawAgent] = useState(null) as any;
    const [isFirstEffectDone, setIsFirstEffectDone] = useState(false);
    const [defaultRawVesselIdentifier, setDefaultRawVesselIdentifier] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(false);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Submit Status Nomination?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Submit Status Inspection Nomination?");
        setConfirmationTest("Are you sure you want to submit?");
        setConfirmationTitleButton("Submit Status");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const obj = {
            id,
            nominationInspectorStatus: dataEdit.nominationInspectorStatus ? parseInt(dataEdit.nominationInspectorStatus) : 0,
            inspectorStatusNotes: dataEdit.inspectorStatusNotes,
            modifiedBy: name
        };
        return obj;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Submit Status Nomination?") {
            navigate('/inspection-nomination');
            return;
        }

        const obj = generateDataEdit();
        const response = await putStatusNominationInspectingCompany(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Nomination submitted.',
                description: "We've submitted the nomination for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/inspection-nomination')
            }, 1000);
        } else {
            toast({
                title: 'Nomination failed to submit.',
                description: "We unabled to submit the status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeCompany = (value: string, props: any) => {
        const rawData = listRawInspector.find((x: any) => x.inspectorId === value);
        props.setFieldValue('companyName', value);
        props.setFieldValue('companyInspectionAddressDetail', rawData?.address);
        props.setFieldValue('companyInspectionPhoneDetail', rawData?.phone);
    }

    const handleChangeVessel = (value: string, props: any) => {
        const rawData = listRawShipName.find((x: any) => x.vesselId === value);
        props.setFieldValue('vesselIdentifier', rawData?.imoreg);
        props.setFieldValue('vesselIdentifierType', rawData?.vesselIdentifierType);
        props.setFieldValue('companyName', value);
        props.setFieldValue('nameOfVessel', value);
    }

    const handleChangeType = (props: any, value: string) => {
        props.setFieldValue('groupLocation', '');
        props.setFieldValue('awaitingDaysFee', 0);
        props.setFieldValue('companyType', value);
        props.setFieldValue('fee', 0);
        getDataListCompanyByType(value);
    }

    /*const handleChangeAgent = (props: any, e: any) => {
        if (e === null) {
            props.setFieldValue('agent', '');
            props.setFieldValue('agentName', '');
            props.setFieldValue('agentOfficePhone', '');
            props.setFieldValue('agentMobilePhone', '');
            props.setFieldValue('agentFax', '');
            props.setFieldValue('agentEmailAddress', '');
            setIsAgentDisabled(false);
            return;
        }

        const dataAgent = listRawAgent?.find((x: any) => x.id === e.value);
        props.setFieldValue('agent', e.value);
        props.setFieldValue('agentName', dataAgent ? dataAgent.name : '');
        props.setFieldValue('agentOfficePhone', dataAgent ? dataAgent.officePhone : '');
        props.setFieldValue('agentMobilePhone', dataAgent ? dataAgent.mobilePhone : '');
        props.setFieldValue('agentFax', dataAgent ? dataAgent.fax : '');
        props.setFieldValue('agentEmailAddress', dataAgent ? dataAgent.email : '');

        setIsAgentDisabled(!!dataAgent);
    }*/

    const getListShipName = async () => {
        const response = await getDataVessel() as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawShipName(response.data);
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        }
    }

    const getListAgent = async () => {
        const response = await getDataAgent() as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawAgent(response.data);
            const formattedOptions = response.data.map((item: any) => ({ value: item.id, label: item.name }));
            setListAgent(formattedOptions);
        } else {
            setListRawAgent([]);
            setListAgent([]);
        }
    }

    const getDataListCompanyByType = async (value: string, init = false, id = '') => {
        const params = {
            sire: value === 'SIRE' ? true : false,
            bire: value === 'BIRE' ? true : false,
            vetting: value === 'Vetting' ? true : false,
        };

        const response = await getDataInspectorByType(params) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListRawInspector(response.data);
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorId,
                label: item.inspectorName,
            }));
            setListInspectorCompany(formattedData);
            if (init) {
                const rawData = response.data.find((x: any) => x.inspectorId === id);
                setDefaultCompanyInspectionAddressDetail(rawData.address);
                setDefaultCompanyInspectionPhoneDetail(rawData.phone);
            }
        } else {
            toast({
                title: 'Failed to get data inspectors.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorCompany([]);
        }
    }

    const getDataListInspectorPerson = async () => {
        const response = await getInspectorPersonData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorPersonId,
                label: item.inspectorPersonName,
            }));
            setListInspectorPerson(formattedData);
        } else {
            toast({
                title: 'Failed to get data inspector person.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorPerson([]);
        }
    }

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            } else if (type === 'siretype') {
                setListSireType(formattedData);
            } else if (type === 'cargo') {
                setListCargo(formattedData);
            } else if (type === 'meansofaccess') {
                setListMeansOfAccess(formattedData);
            } else if (type === 'typeofvessel') {
                setListTypeOfVessels(formattedData);
            } else if (type === 'operation') {
                setListOperations(formattedData);
            }
        } else {
            toast({
                title: 'Failed to get data ref.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListMarket([]);
            setListSireType([]);
            setListCargo([]);
            setListMeansOfAccess([]);
            setListTypeOfVessels([]);
            setListOperations([]);
        }
    }

    const getDataDetail = async () => {
        const response = await getNominationById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);

            const rawAgent = listRawAgent.find((x: any) => x.id === response.data.agentId);
            setDefaultRawAgent(rawAgent);
            //setIsAgentDisabled(true);

            const rawVessel = listRawShipName.find((x: any) => x.vesselId === response.data.vesselId);
            setDefaultRawVesselIdentifier(rawVessel);

            if (response.data.sire) {
                setDefaultType('SIRE');
                getDataListCompanyByType('sire', true, response.data.inspectingCompanyId);
            }

            if (response.data.bire) {
                setDefaultType('BIRE');
                getDataListCompanyByType('bire', true, response.data.inspectingCompanyId);
            }

            if (response.data.vetting) {
                setDefaultType('Vetting');
                getDataListCompanyByType('vetting', true, response.data.inspectingCompanyId);
            }

            setIsLoading(false);
        } else {
            setIsLoading(false);
            toast({
                title: 'Failed to load nomination data.',
                description: response.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await getListShipName();
                await getListRef('typeofvessel');
                await getListRef('operation');
                await getListRef('market');
                await getListRef('siretype');
                await getListRef('cargo');
                await getListRef('meansofaccess');
                await getListAgent();
                await getDataListInspectorPerson();

                setIsFirstEffectDone(true);
            } catch (error) {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (id && isFirstEffectDone) {
            getDataDetail();
        }
    }, [id, isFirstEffectDone]);

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                companyType: dataDetail ? defaultType : '',
                                companyName: dataDetail ? dataDetail.inspectingCompanyId : '',
                                companyInspectionAddressDetail: defaultCompanyInspectionAddressDetail,
                                companyInspectionPhoneDetail: defaultCompanyInspectionPhoneDetail,
                                inspectionDate: dataDetail ? dataDetail.inspectionDate : '',
                                location: dataDetail ? dataDetail.country : '',
                                requestDate: dataDetail ? dataDetail.requestDate : '',
                                market: dataDetail ? dataDetail.market : '',
                                nominationType: dataDetail ? dataDetail.type : '',
                                agent: dataDetail ? dataDetail.agentId : '',
                                agentName: defaultRawAgent ? defaultRawAgent.name : '',
                                agentOfficePhone: defaultRawAgent ? defaultRawAgent.officePhone : '',
                                agentMobilePhone: defaultRawAgent ? defaultRawAgent.mobilePhone : '',
                                agentFax: defaultRawAgent ? defaultRawAgent.fax : '',
                                agentEmailAddress: defaultRawAgent ? defaultRawAgent.email : '',
                                nameOfVessel: dataDetail ? dataDetail.vesselId : '',
                                vesselIdentifierType: dataDetail ? defaultRawVesselIdentifier.vesselIdentifierType : '',
                                vesselIdentifier: dataDetail ? defaultRawVesselIdentifier.imoreg : '',
                                typeOfVessel: dataDetail ? dataDetail.typeOfvessel : '',
                                ETAVessel: dataDetail ? dataDetail.vesselETA : '',
                                portOfBerth: dataDetail ? dataDetail.portOfBerth : '',
                                countryOfInspection: dataDetail ? dataDetail.countryOfInspection : '',
                                portOfInspection: dataDetail ? dataDetail.portOfInspection : '',
                                ETA: dataDetail ? dataDetail.eta : '',
                                ETB: dataDetail ? dataDetail.etb : '',
                                ETD: dataDetail ? dataDetail.etd : '',
                                operation: dataDetail ? dataDetail.operation : '',
                                cargo: dataDetail ? dataDetail.cargo : '',
                                meansOfAccess: dataDetail ? dataDetail.meansOfAccess : '',
                                inspectorName: dataDetail ? dataDetail.inspectorId : '',
                                codeBook: dataDetail ? dataDetail.codeBook : '',
                                groupLocation: dataDetail ? dataDetail.inspectionFeeId : '',
                                fee: dataDetail ? dataDetail.fee : '',
                                awaitingDaysFee: dataDetail ? dataDetail.awaitingDaysFee : '',
                                nominationInspectorStatus: '',
                                inspectorStatusNotes: ''
                            }}
                            validationSchema={inspectionNominationApprovalSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='companyType'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.companyType && form.touched.companyType} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCompanyFullType name='companyType' handleChange={({ value }: any) => handleChangeType(props, value)} value={props.values.companyType} defaultValue={props.values.companyType} hasError={form.errors.companyType && form.touched.companyType} isDisabled />
                                                        <FormErrorMessage>{form.errors.companyType}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyName'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.companyName && form.touched.companyName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company Name</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='companyName' handleChange={({ value }: any) => handleChangeCompany(value, props)} value={props.values.companyName} defaultValue={props.values.companyName} options={listInspectorCompany} hasError={form.errors.companyName && form.touched.companyName} isDisabled />
                                                        <FormErrorMessage>{form.errors.companyName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyInspectionAddressDetail'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.companyInspectionAddressDetail && form.touched.companyInspectionAddressDetail} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company Inspection Detail</FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} className="form-input" value={props.values.companyInspectionAddressDetail} disabled />
                                                        <FormErrorMessage>{form.errors.companyInspectionAddressDetail}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='companyInspectionPhoneDetail'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.companyInspectionPhoneDetail && form.touched.companyInspectionPhoneDetail} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label"></FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} className="form-input" value={props.values.companyInspectionPhoneDetail} disabled />
                                                        <FormErrorMessage>{form.errors.companyInspectionPhoneDetail}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectionDate'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.inspectionDate && form.touched.inspectionDate} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspection Date</FormLabel>
                                                    <div className={form.errors.inspectionDate && form.touched.inspectionDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="inspectionDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('inspectionDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Inspection Date"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.inspectionDate}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='location'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.location && form.touched.location} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Location</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='location' handleChange={({ value }: any) => props.setFieldValue('location', value)} value={props.values.location} hasError={form.errors.location && form.touched.location} defaultValue={props.values.location} isDisabled />
                                                        <FormErrorMessage>{form.errors.location}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider mb={5} />
                                    <Field name='requestDate'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.requestDate && form.touched.requestDate} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Request Date</FormLabel>
                                                    <div className={form.errors.requestDate && form.touched.requestDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="requestDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('requestDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Request Date"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.requestDate}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='market'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.market && form.touched.market} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Market</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='market' handleChange={({ value }: any) => props.setFieldValue('market', value)} value={props.values.market} hasError={form.errors.market && form.touched.market} defaultValue={props.values.market} options={listMarket} isDisabled />
                                                        <FormErrorMessage>{form.errors.market}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='nominationType'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.nominationType && form.touched.nominationType} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='nominationType' handleChange={({ value }: any) => props.setFieldValue('nominationType', value)} value={props.values.nominationType} hasError={form.errors.nominationType && form.touched.nominationType} defaultValue={props.values.nominationType} options={listSireType} isDisabled />
                                                        <FormErrorMessage>{form.errors.nominationType}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Agent Details</Heading>
                                    <Divider mb={5} />
                                    <Field name='agent'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.agent && form.touched.agent} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectAgent name='agent' /*handleChange={(e: any) => handleChangeAgent(props, e)}*/ value={props.values.agent} hasError={form.errors.agent && form.touched.agent} defaultValue={props.values.agent} options={listAgent} isDisabled />
                                                        <FormErrorMessage>{form.errors.agent}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentName'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.agentName && form.touched.agentName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Name</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Name' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.agentName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentOfficePhone'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.agentOfficePhone && form.touched.agentOfficePhone} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Office Phone</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Office Phone' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.agentOfficePhone}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentMobilePhone'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.agentMobilePhone && form.touched.agentMobilePhone} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Mobile Phone</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Mobile Phone' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.agentMobilePhone}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentFax'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.agentFax && form.touched.agentFax} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Fax</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Fax' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.agentFax}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='agentEmailAddress'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.agentEmailAddress && form.touched.agentEmailAddress} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Agent Email Address</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Agent Email Address' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.agentEmailAddress}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Vessel Details</Heading>
                                    <Divider mb={5} />
                                    <Field name='nameOfVessel'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.nameOfVessel && form.touched.nameOfVessel} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Name of Vessel</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectShip name='nameOfVessel' placeholder="Choose Vessel" handleChange={({ value }: any) => handleChangeVessel(value, props)} value={props.values.nameOfVessel} defaultValue={props.values.nameOfVessel} options={listShipName} hasError={form.errors.nameOfVessel && form.touched.nameOfVessel} isDisabled />
                                                        <FormErrorMessage>{form.errors.nameOfVessel}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselIdentifierType'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.vesselIdentifierType && form.touched.vesselIdentifierType} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Identifier Type</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Vessel Identifier Type' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.vesselIdentifierType}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselIdentifier'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.vesselIdentifier && form.touched.vesselIdentifier} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Identifier</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Vessel Identifier' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.vesselIdentifier}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='typeOfVessel'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.typeOfVessel && form.touched.typeOfVessel} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type of Vessel</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectVesselType name='typeOfVessel' handleChange={({ value }: any) => props.setFieldValue('typeOfVessel', value)} value={props.values.typeOfVessel} hasError={form.errors.typeOfVessel && form.touched.typeOfVessel} defaultValue={props.values.typeOfVessel} options={listTypeOfVessels} isDisabled />
                                                        <FormErrorMessage>{form.errors.typeOfVessel}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETAVessel'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.ETAVessel && form.touched.ETAVessel} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETA</FormLabel>
                                                    <div className={form.errors.ETAVessel && form.touched.ETAVessel ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETAVessel"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETAVessel', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETA"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.ETAVessel}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='portOfBerth'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.portOfBerth && form.touched.portOfBerth} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Port of Berth</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Port of Berth' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.portOfBerth}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Inspection Details</Heading>
                                    <Divider mb={5} />
                                    <Field name='countryOfInspection'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.countryOfInspection && form.touched.countryOfInspection} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Country of Inspection</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='countryOfInspection' handleChange={({ value }: any) => props.setFieldValue('countryOfInspection', value)} value={props.values.countryOfInspection} hasError={form.errors.countryOfInspection && form.touched.countryOfInspection} defaultValue={props.values.countryOfInspection} isDisabled />
                                                        <FormErrorMessage>{form.errors.countryOfInspection}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='portOfInspection'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.portOfInspection && form.touched.portOfInspection} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Port of Inspection</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Port of Inspection' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.portOfInspection}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETA'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.ETA && form.touched.ETA} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETA</FormLabel>
                                                    <div className={form.errors.ETA && form.touched.ETA ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETA"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETA', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETA"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.ETA}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETB'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.ETB && form.touched.ETB} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETB</FormLabel>
                                                    <div className={form.errors.ETB && form.touched.ETB ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETB"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETB', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETB"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.ETB}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='ETD'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.ETD && form.touched.ETD} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ETD</FormLabel>
                                                    <div className={form.errors.ETD && form.touched.ETD ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                        <DatePicker
                                                            id="ETD"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('ETD', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Input ETD"
                                                            readOnly={isSubmitting}
                                                            disabled
                                                        />
                                                        <FormErrorMessage className="form-date-picker-error-message">{form.errors.ETD}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='operation'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.operation && form.touched.operation} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Operation</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectOperationType name='operation' handleChange={({ value }: any) => props.setFieldValue('operation', value)} value={props.values.operation} hasError={form.errors.operation && form.touched.operation} defaultValue={props.values.operation} options={listOperations} isDisabled />
                                                        <FormErrorMessage>{form.errors.operation}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='cargo'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.cargo && form.touched.cargo} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Cargo</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='cargo' handleChange={({ value }: any) => props.setFieldValue('cargo', value)} value={props.values.cargo} hasError={form.errors.cargo && form.touched.cargo} defaultValue={props.values.cargo} options={listCargo} isDisabled />
                                                        <FormErrorMessage>{form.errors.cargo}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='meansOfAccess'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.meansOfAccess && form.touched.meansOfAccess} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Means of Access</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectMarketType name='meansOfAccess' handleChange={({ value }: any) => props.setFieldValue('meansOfAccess', value)} value={props.values.meansOfAccess} hasError={form.errors.meansOfAccess && form.touched.meansOfAccess} defaultValue={props.values.meansOfAccess} options={listMeansOfAccess} isDisabled />
                                                        <FormErrorMessage>{form.errors.meansOfAccess}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectorName'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.inspectorName && form.touched.inspectorName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspector Name</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='inspectorName' handleChange={({ value }: any) => props.setFieldValue('inspectorName', value)} value={props.values.inspectorName} defaultValue={props.values.inspectorName} options={listInspectorPerson} hasError={form.errors.inspectorName && form.touched.inspectorName} isDisabled />
                                                        <FormErrorMessage>{form.errors.inspectorName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='codeBook'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.codeBook && form.touched.codeBook} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Code Book</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Code Book' className="form-input" disabled />
                                                        <FormErrorMessage>{form.errors.codeBook}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider mb={5} />

                                    <Field name='nominationInspectorStatus'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.nominationInspectorStatus && form.touched.nominationInspectorStatus} style={{ marginBottom: 15, alignItems: 'center' }}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor='nominationInspectorStatus-switch' className="form-label">
                                                        Status<span style={{ color: 'red' }}>*</span>
                                                    </FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectNominationApprovalStatus name='nominationInspectorStatus' handleChange={({ value }: any) => props.setFieldValue('nominationInspectorStatus', value)} value={props.values.nominationInspectorStatus} hasError={form.errors.nominationInspectorStatus && form.touched.nominationInspectorStatus} defaultValue={props.values.nominationInspectorStatus} />
                                                        <FormErrorMessage>{form.errors.nominationInspectorStatus}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {props.values.nominationInspectorStatus === "2" &&
                                        <Field name='inspectorStatusNotes'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.inspectorStatusNotes && form.submitCount > 0} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Note<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} placeholder='Note' className="form-input" isInvalid={form.errors.inspectorStatusNotes && form.submitCount > 0} />
                                                        <FormErrorMessage>{form.errors.inspectorStatusNotes}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    }

                                    <Divider />

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default InspectingNominationReview;
